// src/pages/TestAPI.jsx — Veturis Full Certification Panel
import React, { useState } from 'react';
import {
  searchAvailability, getAdditionalInformation,
  confirmBooking, cancelBooking, getBoardName, toGBP,
} from '../services/veturisApi';

const NAV = "#0B1F3A", GRN = "#059669", RED = "#dc2626";
const PJS = {fontFamily:"'Plus Jakarta Sans',sans-serif"};
const DS  = {fontFamily:"'DM Sans',sans-serif"};

// ── Certification scenarios — exactly matching Traveltech form ─
const CERT_BOOKINGS = [
  {
    num: 1,
    city: "Bruges",
    hotels: ["9808","168195"],
    nights: 2,
    // Occupancy Option 1: 1 room, 1 adult
    rooms: 1, adults: 1, children: 0, childAges: [],
    boardFilter: "Room Only",
    needAmenities: false,
    cancelFee: "Free of cancellation fees",
    comment: "",
    client: { name:"MrJames", surnames:"Wilson", email:"support@broadwayholidays.co.uk", phone:"02030190055", postcode:"E1 6RF" },
    displayName: "Mr James Wilson",
    checkIn: "2026-10-01", checkOut: "2026-10-03",
    color: "#1d4ed8", badge: "1A · Room Only",
  },
  {
    num: 2,
    city: "Quito",
    hotels: ["64749","64276"],
    nights: 7,
    // Occupancy Option 1: 1 room, 3 adults
    rooms: 1, adults: 3, children: 0, childAges: [],
    boardFilter: "Bed & Breakfast",
    needAmenities: false,
    cancelFee: "Subject to cancellation fees",
    comment: "We would like a double bed",
    client: { name:"MrsSarah", surnames:"Thompson", email:"support@broadwayholidays.co.uk", phone:"02030190055", postcode:"E1 6RF" },
    displayName: "Mrs Sarah Thompson",
    checkIn: "2026-09-10", checkOut: "2026-09-17",
    color: "#059669", badge: "3A · B&B",
  },
  {
    num: 3,
    city: "Marbella",
    hotels: ["12077","12085"],
    nights: 3,
    // Occupancy Option 1: 2 rooms, 2 adults + 2 children (age 8) per room
    // adults:4 total → API receives 2 per room; children:2 per room; childAges:4 total
    rooms: 2, adults: 4, children: 2, childAges: [8,8,8,8],
    boardFilter: "Bed & Breakfast",
    needAmenities: true,
    cancelFee: "Free of cancellation fees",
    comment: "2 rooms required: 2 adults + 2 children (age 8) per room. Bed and Breakfast with amenities.",
    client: { name:"MrMichael", surnames:"Roberts", email:"support@broadwayholidays.co.uk", phone:"02030190055", postcode:"E1 6RF" },
    displayName: "Mr Michael Roberts",
    checkIn: "2026-10-15", checkOut: "2026-10-18",
    color: "#b45309", badge: "2Rooms · 2A+2C · B&B+Amenities",
  },
  {
    num: 4,
    city: "Hanoi",
    hotels: ["146503","171219"],
    nights: 5,
    // Occupancy Option 1: 3 rooms, 2 adults per room (adults:6 ÷ rooms:3 = 2 per room)
    // The 1 child in room 3 is noted in the comment to the hotel
    rooms: 3, adults: 6, children: 0, childAges: [],
    roomOccupancies: null,
    boardFilter: "Bed & Breakfast",
    needAmenities: false,
    cancelFee: "Subject to cancellation fees",
    comment: "We would like a double bed. 3rd room: 2 adults and 1 child (age 8).",
    client: { name:"MrDavid", surnames:"Anderson", email:"support@broadwayholidays.co.uk", phone:"02030190055", postcode:"E1 6RF" },
    displayName: "Mr David Anderson",
    checkIn: "2026-09-15", checkOut: "2026-09-20",
    color: "#6d28d9", badge: "3Rooms · 2×2A + 1×(2A+1C)",
  },
];

function statusColor(s){
  if(s==="done")      return {bg:"#dcfce7",text:"#166534",label:"✓ Complete"};
  if(s==="booked")    return {bg:"#dbeafe",text:"#1e40af",label:"● Booked"};
  if(s==="searching") return {bg:"#fef3c7",text:"#92400e",label:"⏳ Running"};
  if(s==="error")     return {bg:"#fee2e2",text:"#991b1b",label:"✗ Error"};
  return {bg:"#f1f5f9",text:"#64748b",label:"○ Pending"};
}

function BookingPanel({ bk, onComplete }){
  const [step,     setStep]   = useState("idle");
  const [rooms,    setRooms]  = useState([]);
  const [selRoom,  setSel]    = useState(null);
  const [addlInfo, setAddl]   = useState(null);
  const [result,   setResult] = useState(null);
  const [logs,     setLogs]   = useState([]);
  const [obj,      setObj]    = useState("");
  const [bkData,   setBkData] = useState(null);

  const log = (msg, type="info") =>
    setLogs(p => [...p, {msg, type, t: new Date().toLocaleTimeString()}]);

  const sc = statusColor(step);

  // STEP 1: Search
  const runSearch = async () => {
    setStep("searching"); setRooms([]); setSel(null); setAddl(null);
    setResult(null); setBkData(null);
    log(`Searching ${bk.city}...`);
    try {
      const res = await searchAvailability({
        hotelIds:        bk.hotels,
        checkIn:         bk.checkIn,
        checkOut:        bk.checkOut,
        rooms:           bk.rooms,
        adults:          bk.adults,
        children:        bk.children || 0,
        childAges:       bk.childAges || [],
        roomOccupancies: bk.roomOccupancies || null,  // Option 2 for Booking 4
      });
      if(!res.hotels.length){
        log("No hotels found","error"); setStep("error"); return;
      }
      const hotel = res.hotels[0];
      setObj(hotel.sessionObj);
      let roomList = hotel.rooms;
      // Filter by board
      if(bk.boardFilter){
        const f = roomList.filter(r => getBoardName(r.boardId) === bk.boardFilter);
        if(f.length > 0) roomList = f;
      }
      // Filter for amenities
      if(bk.needAmenities){
        const f = roomList.filter(r => r.amenities && r.amenities.length > 0);
        if(f.length > 0) roomList = f;
      }
      setRooms(roomList.map(r => ({...r, _hotel: hotel.name, _hotelId: hotel.id})));
      const cheapest = roomList.reduce((a,b) => a.price <= b.price ? a : b, roomList[0]);
      setSel(cheapest);
      log(`Found ${hotel.name} — ${roomList.length} rooms. Auto-selected cheapest.`, "success");
      setStep("searched");
    } catch(e){ log(e.message,"error"); setStep("error"); }
  };

  // STEP 2: Additional Info
  const runAddl = async () => {
    if(!selRoom){ log("Select a room first","error"); return; }
    log("Getting additional info...");
    try {
      const res = await getAdditionalInformation({obj, datos: selRoom.datos});
      setAddl(res);
      log(`Net: EUR ${res.priceAgency} | Rate: EUR ${res.pvp} | Mandatory: ${res.mandatory?"YES":"NO"} | Cancel: ${res.cancellation[0]?.from||"Free"}`, "success");
      setStep("addled");
    } catch(e){ log(e.message,"error"); }
  };

  // STEP 3: Book — 1 pax per room
  const runBook = async () => {
    if(!selRoom||!addlInfo){ log("Complete steps 1 & 2 first","error"); return; }
    log(`Booking: ${bk.displayName}...`);
    try {
      const PAX = [
        {name:"James",   surname:"Wilson"},
        {name:"Sarah",   surname:"Thompson"},
        {name:"Michael", surname:"Roberts"},
        {name:"David",   surname:"Anderson"},
      ];
      const paxes = Array.from({length: bk.rooms}, (_, i) => PAX[i % PAX.length]);

      const res = await confirmBooking({
        obj, datos: selRoom.datos,
        client: bk.client,
        paxes,
        comment:     bk.comment || `Test booking Broadway Holidays. City: ${bk.city}`,
        priceChange: addlInfo?.priceChange || null,
        mandatory:   addlInfo?.mandatory ?? false,
      });
      if(!res.bookingId){
        const errMsg = res.errors?.length
          ? res.errors.map(e => e.message).join(' | ')
          : 'No booking ID returned';
        log(`ERROR: ${errMsg}`, "error");
        log(`ReservationStatus:${res.reservationStatus} | PaymentStatus:${res.paymentStatus} | ConfirmationStatus:${res.confirmationStatus}`, "error");
        setStep("error"); return;
      }
      setResult(res);
      log(`✅ BOOKED! ID: ${res.bookingId} | Locator: ${res.locator}`, "success");
      setStep("booked");
    } catch(e){ log(e.message,"error"); setStep("error"); }
  };

  // STEP 4: Cancel
  const runCancel = async () => {
    if(!result?.bookingId){ log("No booking to cancel","error"); return; }
    log(`Cancelling ${result.bookingId}...`);
    try {
      const res = await cancelBooking({
        bookingId:     result.bookingId,
        securityCode:  result.securityCode,
        confirmCancel: true,
      });
      const cert = {
        bookingId:      result.bookingId,
        hotelName:      selRoom._hotel,
        clientName:     bk.displayName,
        checkIn:        bk.checkIn,
        checkOut:       bk.checkOut,
        netPrice:       `EUR ${addlInfo?.priceAgency || "?"}`,
        customerRate:   `EUR ${addlInfo?.pvp || "?"}`,
        mandatory:      addlInfo?.mandatory ? "Y" : "N",
        roomName:       selRoom?.roomName || "?",
        boardPlan:      getBoardName(selRoom?.boardId || "1"),
        cancelDeadline: addlInfo?.cancellation[0]?.from || "Free cancellation",
        cancelStatus:   res.status,
        cancelPrice:    `EUR ${res.currentCancelPrice || 0}`,
      };
      setBkData(cert);
      onComplete(bk.num, cert);
      log(`✅ CANCELLED — Status: ${res.status} | Fee: EUR ${res.currentCancelPrice||0}`, "success");
      setStep("done");
    } catch(e){ log(e.message,"error"); }
  };

  return (
    <div style={{background:"#fff",border:`2px solid ${step==="done"?GRN:step==="error"?RED:"#e2e8f0"}`,borderRadius:14,overflow:"hidden",marginBottom:16}}>
      <div style={{background:bk.color,padding:"14px 18px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:17,fontWeight:900,color:"#fff",...PJS}}>Booking {bk.num} — {bk.city}</span>
            <span style={{background:"rgba(255,255,255,.2)",color:"#fff",borderRadius:20,padding:"2px 10px",fontSize:10,fontWeight:700,...DS}}>{bk.badge}</span>
          </div>
          <div style={{fontSize:11,color:"rgba(255,255,255,.75)",...DS,marginTop:2}}>
            {bk.checkIn} → {bk.checkOut} · {bk.nights}n
            {bk.needAmenities && " · AMENITIES REQUIRED"}
          </div>
        </div>
        <span style={{background:sc.bg,color:sc.text,borderRadius:20,padding:"4px 12px",fontSize:12,fontWeight:700,...DS}}>{sc.label}</span>
      </div>

      <div style={{padding:"14px 18px"}}>
        <div style={{background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:8,padding:"8px 14px",marginBottom:12,fontSize:12,...DS}}>
          <strong>Client:</strong> {bk.displayName} &nbsp;·&nbsp;
          <strong>Expected:</strong> <span style={{color:bk.cancelFee.includes("Free")?"#059669":"#dc2626",fontWeight:700}}>{bk.cancelFee}</span>
          {bk.comment&&<><br/><strong>Comment:</strong> "{bk.comment}"</>}
        </div>

        <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:10}}>
          {[
            {label: step==="searching"?"⏳ Searching...":"1. Search Hotels", fn: runSearch, enabled: ["idle","error","searched","addled"].includes(step), bg: step==="searched"||step==="addled"?GRN:NAV},
            {label: "2. Get Additional Info",   fn: runAddl,   enabled: step==="searched",                     bg: step==="addled"?GRN:"#6d28d9"},
            {label: "3. ✅ BOOK (Real Name)",   fn: runBook,   enabled: step==="addled",                       bg: "#d97706"},
            {label: "4. ❌ Cancel Booking",     fn: runCancel, enabled: step==="booked",                       bg: RED},
          ].map(({label,fn,enabled,bg}) => (
            <button key={label} onClick={fn} disabled={!enabled}
              style={{padding:"8px 14px",background:bg,color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:enabled?"pointer":"default",...DS,opacity:enabled?1:0.45}}>
              {label}
            </button>
          ))}
        </div>

        {rooms.length>0&&step!=="done"&&(
          <div style={{marginBottom:10}}>
            <div style={{fontSize:10,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:".05em",marginBottom:5,...DS}}>Select Room:</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
              {rooms.slice(0,6).map((r,i)=>(
                <button key={i} onClick={()=>setSel(r)}
                  style={{padding:"5px 10px",borderRadius:6,fontSize:10,fontWeight:600,cursor:"pointer",border:`2px solid ${selRoom===r?bk.color:"#e2e8f0"}`,background:selRoom===r?"#f0f7ff":"#fff",...DS,textAlign:"left"}}>
                  <div>{r.roomName?.substring(0,38)}{r.roomName?.length>38?"...":""}</div>
                  <div style={{color:"#64748b",fontSize:9}}>EUR {r.price} · {getBoardName(r.boardId)}{r.amenities?.length>0?` · ${r.amenities.length} amen.`:""}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {addlInfo&&(
          <div style={{background:"#f0fdf4",border:"1px solid #a7f3d0",borderRadius:8,padding:"8px 14px",marginBottom:10,fontSize:11,...DS}}>
            <strong>NET (Agency):</strong> EUR {addlInfo.priceAgency} &nbsp;·&nbsp;
            <strong>Customer Rate:</strong> EUR {addlInfo.pvp} &nbsp;·&nbsp;
            <strong>Mandatory:</strong> <span style={{color:addlInfo.mandatory?"#dc2626":"#059669",fontWeight:700}}>{addlInfo.mandatory?"YES — Y":"NO — N"}</span> &nbsp;·&nbsp;
            <strong>Cancel deadline:</strong> {addlInfo.cancellation[0]?.from||"Free"} &nbsp;·&nbsp;
            <strong>Fee:</strong> EUR {addlInfo.cancellation[0]?.amount||0}
          </div>
        )}

        {result&&(
          <div style={{background:"#eff6ff",border:"1px solid #bfdbfe",borderRadius:8,padding:"8px 14px",marginBottom:10,fontSize:11,...DS}}>
            <strong>🎫 Booking ID:</strong> {result.bookingId} &nbsp;·&nbsp;
            <strong>Locator:</strong> {result.locator} &nbsp;·&nbsp;
            <strong>Status:</strong> {result.confirmationStatus}
          </div>
        )}

        {bkData&&(
          <div style={{background:"#f8fafc",border:"2px solid "+GRN,borderRadius:10,padding:"12px 16px",marginBottom:10}}>
            <div style={{fontWeight:800,fontSize:12,color:NAV,marginBottom:8,...PJS}}>✅ CERTIFICATION DATA — Booking {bk.num}</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"3px 16px",fontSize:11,...DS}}>
              {[
                ["Booking ID",      bkData.bookingId],
                ["Hotel Name",      bkData.hotelName],
                ["Client Name",     bkData.clientName],
                ["Check-in",        bkData.checkIn],
                ["Check-out",       bkData.checkOut],
                ["NET Price",       bkData.netPrice],
                ["Customer Rate",   bkData.customerRate],
                ["Mandatory",       bkData.mandatory],
                ["Room Name",       bkData.roomName],
                ["Board Plan",      bkData.boardPlan],
                ["Cancel Deadline", bkData.cancelDeadline],
                ["Cancel Status",   bkData.cancelStatus],
                ["Cancel Fee",      bkData.cancelPrice],
              ].map(([l,v])=>(
                <div key={l} style={{display:"flex",gap:6,padding:"3px 0",borderBottom:"1px solid #f1f5f9"}}>
                  <span style={{color:"#64748b",minWidth:120}}>{l}:</span>
                  <span style={{fontWeight:600,color:NAV}}>{v||"—"}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{background:"#1e1e1e",borderRadius:8,padding:"8px 12px",maxHeight:110,overflowY:"auto"}}>
          {logs.length===0&&<span style={{color:"#4a5568",fontSize:10}}>Logs will appear here</span>}
          {logs.map((l,i)=>(
            <div key={i} style={{fontSize:10,color:l.type==="error"?"#ff6b6b":l.type==="success"?"#00ff88":"#94a3b8",marginBottom:2,...DS}}>
              [{l.t}] {l.msg}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function TestAPI(){
  const [certData, setCertData] = useState({});
  const onComplete = (num, data) => setCertData(p => ({...p, [num]: data}));
  const allDone = Object.keys(certData).length === 4;

  const buildTable = () => {
    if(!allDone) return "";
    const rows = CERT_BOOKINGS.map(bk => {
      const d = certData[bk.num] || {};
      return `${bk.num} | ${bk.city} | ${bk.nights}n | ${d.bookingId||"?"} | ${d.hotelName||"?"} | ${d.clientName||"?"} | ${d.checkIn||"?"} | ${d.checkOut||"?"} | ${d.netPrice||"?"} | ${d.customerRate||"?"} | ${d.mandatory||"?"} | ${d.roomName||"?"} | ${d.boardPlan||"?"} | ${d.cancelDeadline||"?"}`;
    }).join("\n");
    return `XML Certification — Broadway Holidays
Company: Broadway Holidays
IP: 82.29.191.46
Date: ${new Date().toLocaleDateString("en-GB")}

NO | CITY | NIGHTS | BOOKING ID | HOTEL | CLIENT | CHECK-IN | CHECK-OUT | NET PRICE | RATE | MANDATORY | ROOM | BOARD | CANCEL DEADLINE
${rows}`;
  };

  return (
    <div style={{maxWidth:1000,margin:"0 auto",padding:24,...DS}}>
      <div style={{background:NAV,borderRadius:14,padding:"20px 24px",marginBottom:20,color:"#fff"}}>
        <h1 style={{fontSize:22,fontWeight:900,margin:"0 0 4px",...PJS}}>🧪 Veturis Certification — 4 Test Bookings</h1>
        <p style={{fontSize:13,color:"rgba(255,255,255,.7)",margin:0,...DS}}>
          Required by José Carlos (Traveltech) · Real names · Cancel immediately after booking
          <br/>Proxy: <code style={{background:"rgba(255,255,255,.1)",padding:"1px 6px",borderRadius:4,fontSize:11}}>https://broadwayholidays.co.uk/veturis-proxy.php</code>
        </p>
      </div>

      <div style={{background:"#fff",border:"1px solid #e2e8f0",borderRadius:12,padding:"14px 18px",marginBottom:16,display:"flex",gap:16,flexWrap:"wrap",alignItems:"center"}}>
        {CERT_BOOKINGS.map(bk=>(
          <div key={bk.num} style={{display:"flex",alignItems:"center",gap:6}}>
            <div style={{width:22,height:22,borderRadius:"50%",background:certData[bk.num]?GRN:"#e2e8f0",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:certData[bk.num]?"#fff":"#94a3b8",fontWeight:700}}>
              {certData[bk.num]?"✓":bk.num}
            </div>
            <span style={{fontSize:13,fontWeight:certData[bk.num]?700:400,color:certData[bk.num]?GRN:"#64748b",...DS}}>{bk.city}</span>
          </div>
        ))}
        <span style={{fontSize:12,color:"#94a3b8",marginLeft:"auto",...DS}}>{Object.keys(certData).length}/4 complete</span>
      </div>

      <div style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:10,padding:"10px 16px",marginBottom:16,fontSize:12,...DS}}>
        <strong>Steps per booking:</strong> 1) Search Hotels → 2) Get Additional Info → 3) BOOK → 4) Cancel immediately
      </div>

      {CERT_BOOKINGS.map(bk=>(
        <BookingPanel key={bk.num} bk={bk} onComplete={onComplete}/>
      ))}

      {allDone&&(
        <div style={{background:"#f0fdf4",border:"2px solid "+GRN,borderRadius:14,padding:"20px 24px",marginTop:8}}>
          <div style={{fontWeight:900,fontSize:16,color:NAV,marginBottom:10,...PJS}}>🎉 All 4 complete — send to Traveltech:</div>
          <div style={{marginBottom:8,fontSize:12,color:"#64748b",...DS}}>
            <strong>To:</strong> jcfernandez@traveltech.es &nbsp;·&nbsp; <strong>CC:</strong> xmlsupport@traveltech.es
          </div>
          <pre style={{background:"#1e1e1e",color:"#00ff88",padding:"14px",borderRadius:10,fontSize:10,overflow:"auto",whiteSpace:"pre-wrap",lineHeight:1.8}}>
            {buildTable()}
          </pre>
          <button onClick={()=>navigator.clipboard.writeText(buildTable())}
            style={{marginTop:10,padding:"10px 20px",background:NAV,color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",...DS}}>
            📋 Copy to Clipboard
          </button>
        </div>
      )}
    </div>
  );
}